import React from 'react';
import CachedItemSelect from '../select/CachedItemSelect';
import CachedItemSelectByCode from '../select/CachedItemSelectByCode';
import EmployeeInputSelect from '../select/EmployeeInputSelect';
import { parseDecimalInput, formatForEdit } from '../utils/decimalInput';

const apiUrl = process.env.REACT_APP_API_URL ?? '';

interface NestedTableCellProps {
  field: string;
  row: any;
  rowIndex: number;
  columnOrder: string[];
  inputRefs: React.MutableRefObject<(HTMLInputElement | HTMLSelectElement | null)[][]>;
  localRows: any[];
  setLocalRows: Function;
  getItemById: any;
  itemMetaById: Record<string, any>;
  resetSortingOnEdit: Function;
  handleTableKeyDown: Function;
  calculatePriceValue: Function;
  handleQuantityClick?: (rowIndex: number) => void;
  isLocked?: boolean;
}

const NestedTableCell: React.FC<NestedTableCellProps> = ({
  field,
  row,
  rowIndex,
  columnOrder,
  inputRefs,
  localRows,
  setLocalRows,
  getItemById,
  itemMetaById,
  resetSortingOnEdit,
  handleTableKeyDown,
  calculatePriceValue,
  handleQuantityClick,
  isLocked = false
}) => {
  const getColumnIndex = (field: string) => columnOrder.indexOf(field) + 2;

  // Unit measurement helper: match Ingredients logic (mainUnit → summaryUnit → unitMeasurment → unit).
  // Also guard against accidentally displaying ObjectId strings.
  const isObjectIdLike = (val: unknown) =>
    typeof val === 'string' && /^[a-f\d]{24}$/i.test(val);

  const pickPreferredUnit = (itemOrMeta: any) => {
    if (!itemOrMeta) return null;
    return (
      itemOrMeta.mainUnit ??
      itemOrMeta.summaryUnit ??
      itemOrMeta.unitMeasurment ??
      itemOrMeta.unit ??
      itemOrMeta.unitMeasurement ?? // tolerate legacy naming
      null
    );
  };

  const unitLabelFromUnitObj = (unitObj: any): string => {
    if (!unitObj) return '';
    if (typeof unitObj === 'string') return isObjectIdLike(unitObj) ? '' : unitObj;
    return (
      unitObj.code ||
      unitObj.unitMeasurment ||
      unitObj.unitMeasurement ||
      unitObj.name ||
      unitObj.symbol ||
      ''
    );
  };

  const parseNumber = (value?: string | number): number => parseDecimalInput(value) ?? 0;

  const formatNumber = (value?: string | number) => {
    const num = parseDecimalInput(value);
    if (num === null || num === 0) return "";
    return formatForEdit(num);
  };

  const formatNumberForSave = (value?: string | number) => {
    const num = parseDecimalInput(value);
    if (num === null) return "";
    return formatForEdit(num);
  };


  switch (field) {
    case 'ident':
      return (
        <td key={field} className="border border-gray-300 px-0 py-0 bg-white h-6">
            
          <CachedItemSelectByCode
            value={row.item?._id || ''}
            disabled={isLocked}
            onChange={async (id) => {
              if (!id) return;
              resetSortingOnEdit();
              try {
                const item = await getItemById(id);
                if (item) {
                  const preferredUnitObj = pickPreferredUnit(item);
                  const updated = [...localRows];
                  updated[rowIndex].item = {
                    _id: item._id,
                    name: item.name,
                    ident: item.ident,
                    // Store the preferred unit object so the unit column matches Ingredients behavior.
                    // (If this ends up being an ObjectId string, we will hide it in the renderer.)
                    unitMeasurment: preferredUnitObj || item.unitMeasurment || item.unitMeasurement || ''
                  };
                  // Auto-populate price from purchasePrice if available
                  if (item.purchasePrice && !updated[rowIndex].purchasePrice) {
                    updated[rowIndex].purchasePrice = formatForEdit(item.purchasePrice);
                  }

                  // Fetch valuation price from API
                  try {
                    const token = localStorage.getItem("token");
                    const response = await fetch(`${apiUrl}/api/items/${item._id}/valuation-price`, {
                      headers: { "x-auth-token": token ?? "" }
                    });
                    if (response.ok) {
                      const data = await response.json();
                      if (data.valuationPrice !== null && data.valuationPrice !== undefined) {
                        // Block negative valuation prices - ensure it's always >= 0
                        const safeValuationPrice = Math.max(0, data.valuationPrice);
                        updated[rowIndex].valuationPrice = formatForEdit(safeValuationPrice);
                      } else if (item.purchasePrice) {
                        // Block negative purchasePrice as well
                        const safePurchasePrice = Math.max(0, item.purchasePrice);
                        updated[rowIndex].valuationPrice = formatForEdit(safePurchasePrice);
                      }
                    } else if (item.purchasePrice) {
                        // Block negative purchasePrice as well
                        const safePurchasePrice = Math.max(0, item.purchasePrice);
                        updated[rowIndex].valuationPrice = formatForEdit(safePurchasePrice);
                    }
                  } catch (vpErr) {
                    // Fallback to purchasePrice if valuation price fetch fails
                    if (item.purchasePrice) {
                      // Block negative purchasePrice as well
                      const safePurchasePrice = Math.max(0, item.purchasePrice);
                      updated[rowIndex].valuationPrice = formatForEdit(safePurchasePrice);
                    }
                  }

                  setLocalRows(updated);
                  setTimeout(() => {
                    const nextColumnIndex = getColumnIndex('purchasePrice');
                    inputRefs.current[rowIndex]?.[nextColumnIndex]?.focus();
                  }, 50);
                }
              } catch (err) {
                console.error('Failed to fetch item:', err);
              }
            }}
            onKeyDown={e => handleTableKeyDown(e, rowIndex, getColumnIndex('ident'))}
            ref={el => {
              if (!inputRefs.current[rowIndex]) inputRefs.current[rowIndex] = [];
              inputRefs.current[rowIndex][getColumnIndex('ident')] = el;
            }}
            enableRef={true}
            label={false}
          />
        </td>
      );
    case 'unitMeasurement':
      return (
        <td key={field} className="border border-gray-300 px-2 py-0 bg-gray-50 h-6">
          <div className="w-full h-6 flex items-center justify-center text-xs text-gray-600">
            {(() => {
              // Match Ingredients unit resolution:
              // prefer mainUnit → summaryUnit → unitMeasurment → unit (fallback to meta if backend returns minimal item).
              const itemId = row.item?._id || row.item;
              const meta = itemId ? itemMetaById?.[String(itemId)] : null;
              const preferred =
                pickPreferredUnit(row.item) ||
                pickPreferredUnit(meta);

              const label = unitLabelFromUnitObj(preferred);
              return label || '-';
            })()}
          </div>
        </td>
      );
    case 'name':
      return (
        <td key={field} className="border border-gray-300 px-0 py-0 bg-white h-6">
          <CachedItemSelect
            value={row.item?._id || ''}
            disabled={isLocked}
            onChange={async (id) => {
              if (!id) return;
              resetSortingOnEdit();
              try {
                const item = await getItemById(id);
                if (item) {
                  const preferredUnitObj = pickPreferredUnit(item);
                  const updated = [...localRows];
                  updated[rowIndex].item = {
                    _id: item._id,
                    name: item.name,
                    ident: item.ident,
                    unitMeasurment: preferredUnitObj || item.unitMeasurment || item.unitMeasurement || ''
                  };
                  // Auto-populate price from purchasePrice if available
                  if (item.purchasePrice && !updated[rowIndex].purchasePrice) {
                    updated[rowIndex].purchasePrice = formatForEdit(item.purchasePrice);
                  }

                  // Fetch valuation price from API
                  try {
                    const token = localStorage.getItem("token");
                    const response = await fetch(`${apiUrl}/api/items/${item._id}/valuation-price`, {
                      headers: { "x-auth-token": token ?? "" }
                    });
                    if (response.ok) {
                      const data = await response.json();
                      if (data.valuationPrice !== null && data.valuationPrice !== undefined) {
                        // Block negative valuation prices - ensure it's always >= 0
                        const safeValuationPrice = Math.max(0, data.valuationPrice);
                        updated[rowIndex].valuationPrice = formatForEdit(safeValuationPrice);
                      } else if (item.purchasePrice) {
                        // Block negative purchasePrice as well
                        const safePurchasePrice = Math.max(0, item.purchasePrice);
                        updated[rowIndex].valuationPrice = formatForEdit(safePurchasePrice);
                      }
                    } else if (item.purchasePrice) {
                        // Block negative purchasePrice as well
                        const safePurchasePrice = Math.max(0, item.purchasePrice);
                        updated[rowIndex].valuationPrice = formatForEdit(safePurchasePrice);
                    }
                  } catch (vpErr) {
                    // Fallback to purchasePrice if valuation price fetch fails
                    if (item.purchasePrice) {
                      // Block negative purchasePrice as well
                      const safePurchasePrice = Math.max(0, item.purchasePrice);
                      updated[rowIndex].valuationPrice = formatForEdit(safePurchasePrice);
                    }
                  }

                  setLocalRows(updated);
                  setTimeout(() => {
                    const nextColumnIndex = getColumnIndex('purchasePrice');
                    inputRefs.current[rowIndex]?.[nextColumnIndex]?.focus();
                  }, 50);
                }
              } catch (err) {
                console.error('Failed to fetch item:', err);
              }
            }}
            onKeyDown={e => handleTableKeyDown(e, rowIndex, getColumnIndex('name'))}
            ref={el => {
              if (!inputRefs.current[rowIndex]) inputRefs.current[rowIndex] = [];
              inputRefs.current[rowIndex][getColumnIndex('name')] = el;
            }}
            enableRef={true}
            label={false}
          />
        </td>
      );
    case 'purchasePrice':
      return (
        <td key={field} className="border border-gray-300 px-0 py-0 bg-white h-6">
          <input
            type="text"
            value={row.purchasePrice || ''}
            disabled={isLocked}
            onChange={e => {
              resetSortingOnEdit();
              const rawValue = e.target.value;

              const updated = [...localRows];
              updated[rowIndex].purchasePrice = rawValue;
              updated[rowIndex].priceValue = calculatePriceValue(rawValue, updated[rowIndex].quantity || '');
              setLocalRows(updated);
            }}
            onBlur={e => {
              const updated = [...localRows];
              const formattedPrice = formatNumber(e.target.value);
              updated[rowIndex].purchasePrice = formattedPrice;
              // Calculate with raw values before formatting
              updated[rowIndex].priceValue = calculatePriceValue(e.target.value, updated[rowIndex].quantity || '');
              setLocalRows(updated);
            }}
            onFocus={e => e.target.select()}
            onKeyDown={e => handleTableKeyDown(e, rowIndex, getColumnIndex('purchasePrice'))}
            ref={el => {
              if (!inputRefs.current[rowIndex]) inputRefs.current[rowIndex] = [];
              inputRefs.current[rowIndex][getColumnIndex('purchasePrice')] = el;
            }}
            className="w-full h-6 px-1 text-right bg-transparent border-0 outline-none focus:bg-blue-50 dark:focus:bg-blue-900/30 focus:ring-2 focus:ring-blue-500/50 dark:focus:ring-blue-600/50"
          />
        </td>
      );
    case 'valuationPrice':
      return (
        <td key={field} className="border border-gray-300 px-0 py-0 bg-gray-50 h-6">
          <input
            type="text"
            value={row.valuationPrice || ''}
            readOnly
            onFocus={e => e.target.select()}
            onKeyDown={e => handleTableKeyDown(e, rowIndex, getColumnIndex('valuationPrice'))}
            ref={el => {
              if (!inputRefs.current[rowIndex]) inputRefs.current[rowIndex] = [];
              inputRefs.current[rowIndex][getColumnIndex('valuationPrice')] = el;
            }}
            className="w-full h-6 px-1 text-right bg-gray-50 border-0 outline-none focus:bg-blue-50 dark:focus:bg-blue-900/30 focus:ring-2 focus:ring-blue-500/50 dark:focus:ring-blue-600/50 text-gray-600"
          />
        </td>
      );
    case 'quantity':
      return (
        <td key={field} className="border border-gray-300 px-0 py-0 bg-gray-50 h-6">
          <input
            type="text"
            value={row.quantity || ''}
            readOnly
            onFocus={e => e.target.select()}
            onKeyDown={e => handleTableKeyDown(e, rowIndex, getColumnIndex('quantity'))}
            ref={el => {
              if (!inputRefs.current[rowIndex]) inputRefs.current[rowIndex] = [];
              inputRefs.current[rowIndex][getColumnIndex('quantity')] = el;
            }}
            className="w-full h-6 px-1 text-right bg-gray-50 border-0 outline-none text-gray-600 cursor-not-allowed"
          />
        </td>
      );
    case 'quantityOS':
      return (
        <td key={field} className="border border-gray-300 px-0 py-0 bg-gray-50 h-6">
          <input
            type="text"
            value={row.quantityOS || ''}
            readOnly
            onFocus={e => e.target.select()}
            onKeyDown={e => handleTableKeyDown(e, rowIndex, getColumnIndex('quantityOS'))}
            ref={el => {
              if (!inputRefs.current[rowIndex]) inputRefs.current[rowIndex] = [];
              inputRefs.current[rowIndex][getColumnIndex('quantityOS')] = el;
            }}
            className="w-full h-6 px-1 text-right bg-gray-50 border-0 outline-none focus:bg-blue-50 dark:focus:bg-blue-900/30 focus:ring-2 focus:ring-blue-500/50 dark:focus:ring-blue-600/50 text-gray-600"
          />
        </td>
      );
    case 'quantityWorkOrder':
      return (
        <td key={field} className="border border-gray-300 px-0 py-0 bg-gray-50 h-6">
          <input
            type="text"
            value={row.quantityWorkOrder || ''}
            readOnly
            className="w-full h-6 px-1 text-right bg-gray-50 border-0 outline-none text-gray-600 cursor-not-allowed"
          />
        </td>
      );
    case 'serial':
      return (
        <td key={field} className="border border-gray-300 px-0 py-0 bg-gray-100 h-6">
          <input
            type="text"
            value={row.serial || ''}
            disabled={isLocked}
            readOnly={true}
            onClick={(e) => {
              // Open serial/quantity modal when clicking on the field (if handler provided and row has an item)
              if (handleQuantityClick && row.item?._id && !isLocked) {
                e.preventDefault();
                handleQuantityClick(rowIndex);
              }
            }}
            onFocus={e => e.target.select()}
            onKeyDown={e => handleTableKeyDown(e, rowIndex, getColumnIndex('serial'))}
            ref={el => {
              if (!inputRefs.current[rowIndex]) inputRefs.current[rowIndex] = [];
              inputRefs.current[rowIndex][getColumnIndex('serial')] = el;
            }}
            className={`w-full h-6 px-1 text-center bg-transparent border-0 outline-none ${handleQuantityClick && row.item?._id && !isLocked ? 'cursor-pointer hover:bg-blue-100' : 'cursor-not-allowed'}`}
            title={handleQuantityClick && row.item?._id ? 'Click to select serial from stock' : ''}
          />
        </td>
      );
    case 'deadline':
      return (
        <td key={field} className="border border-gray-300 px-0 py-0 bg-gray-100 h-6">
          <input
            type="text"
            value={row.deadline || ''}
            disabled={isLocked}
            readOnly={true}
            onClick={(e) => {
              // Open serial/quantity modal when clicking on the field (if handler provided and row has an item)
              if (handleQuantityClick && row.item?._id && !isLocked) {
                e.preventDefault();
                handleQuantityClick(rowIndex);
              }
            }}
            onFocus={e => e.target.select()}
            onKeyDown={e => handleTableKeyDown(e, rowIndex, getColumnIndex('deadline'))}
            ref={el => {
              if (!inputRefs.current[rowIndex]) inputRefs.current[rowIndex] = [];
              inputRefs.current[rowIndex][getColumnIndex('deadline')] = el;
            }}
            className={`w-full h-6 px-1 text-center bg-transparent border-0 outline-none ${handleQuantityClick && row.item?._id && !isLocked ? 'cursor-pointer hover:bg-blue-100' : 'cursor-not-allowed'}`}
            title={handleQuantityClick && row.item?._id ? 'Click to select from stock' : ''}
            placeholder="DD.MM.YYYY"
          />
        </td>
      );
    case 'priceValue':
      return (
        <td key={field} className="border border-gray-300 px-0 py-0 bg-gray-50 h-6">
          <input
            type="text"
            value={row.priceValue || ''}
            readOnly
            onFocus={e => e.target.select()}
            onKeyDown={e => handleTableKeyDown(e, rowIndex, getColumnIndex('priceValue'))}
            ref={el => {
              if (!inputRefs.current[rowIndex]) inputRefs.current[rowIndex] = [];
              inputRefs.current[rowIndex][getColumnIndex('priceValue')] = el;
            }}
            className="w-full h-6 px-1 text-right bg-gray-50 border-0 outline-none focus:bg-blue-50 dark:focus:bg-blue-900/30 focus:ring-2 focus:ring-blue-500/50 dark:focus:ring-blue-600/50 text-gray-600"
          />
        </td>
      );
    case 'employee':
      return (
        <td key={field} className="border border-gray-300 px-0 py-0 bg-white h-6">
          <EmployeeInputSelect
            value={row.employee?._id || ''}
            disabled={isLocked}
            onChange={(employeeId) => {
              resetSortingOnEdit();
              const updated = [...localRows];
              updated[rowIndex].employee = employeeId ? { _id: employeeId, firstName: '', lastName: '' } : null;
              setLocalRows(updated);
            }}
            onKeyDown={e => handleTableKeyDown(e, rowIndex, getColumnIndex('employee'))}
            ref={el => {
              if (!inputRefs.current[rowIndex]) inputRefs.current[rowIndex] = [];
              inputRefs.current[rowIndex][getColumnIndex('employee')] = el;
            }}
            enableRef={true}
            placeholder=""
            inTable={true}
          />
        </td>
      );
    default:
      return null;
  }
};

export default NestedTableCell;